import { DataTable } from "@/components/composables/DataTable";
import {
  useDeleteMerchantUser,
  useGetAllMerchantUsers,
} from "../misc/mutations";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";

import useControl from "@/hooks/useControl";
import { useState } from "react";
import Pagination from "../misc/pagination";
import { Merchant, MerchantUser } from "../misc/types";
import { Button } from "@/components/ui/button";
import ViewMerchantModal from "../misc/viewMerchantModal";
import DeleteMerchantUserModal from "../misc/deleteMerchantUserModal";
import MerchantUserOptions from "../misc/merchantUserOptions";
import MerchantUserFormModal from "../misc/addMerchantUserModal";
import EditMerchantUserModal from "../misc/editMerchantUserModal";
import { Filter12Filled } from "@fluentui/react-icons";

const filterFields = [
  { name: "email", label: "Email", type: "text" },
  { name: "merchantName", label: "Merchant Name", type: "text" },
  { name: "merchantId", label: "Merchant ID", type: "text" },
  { name: "verificationStatus", label: "Verification Status", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "role", label: "Role", type: "text" },
];

const MerchantUserPage = () => {
  const {
    state: isOpenView,
    setTrue: onOpenView,
    setFalse: onCloseView,
  } = useControl();
  const {
    state: isOpenViewModal,
    setTrue: onOpenViewModal,
    setFalse: onCloseViewModal,
  } = useControl();
  const {
    state: isOpenEditModal,
  } = useControl();
  const {
    state: isOpenDeleteModal,
    setTrue: onOpenDeleteModal,
    setFalse: onCloseDeleteModal,
  } = useControl();

  const [currentPage, setCurrentPage] = useState(1);
  const { mutateAsync: deleteMerchantUser, isPending: isPendingDelete } =
    useDeleteMerchantUser({
      onSuccess: () => {
        onCloseDeleteModal();
      },
    });

  const [filters, setFilters] = useState<any>({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFilters((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleSidebarClose = () => setSidebarOpen(false);
  const handleSidebarOpen = () => setSidebarOpen(true);

  const { data: merchantUsers, isPending } = useGetAllMerchantUsers({
    size: 10,
    page: currentPage,
    ...filters,
  });

  const [, setTransactionId] = useState("");
  const [merchantUser, setMerchantUser] = useState<any>();
  const totalPages = merchantUsers?.paginationInfo?.totalPages as number;

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          className="border-[#86929E]"
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          className="border-[#86929E]"
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      cell: ({ row }) => (
        <div>
          {new Date(row.original.createdAt).toLocaleString("en-UK", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}
        </div>
      ),
    },
    {
      accessorKey: "name",
      header: "User Name",
      cell: ({ row }) => <div>{row.original.name}</div>,
    },
    {
      accessorKey: "email",
      header: "User Email",
      cell: ({ row }) => <div>{row.original.email}</div>,
    },
    {
      accessorKey: "phone",
      header: "Status",
      cell: ({ row }) => <div>{row.original.verificationStatus}</div>,
    },
    {
      accessorKey: "merchantName",
      header: "Merchant Name",
      cell: ({ row }) => <div>{row.original.merchantName}</div>,
    },

    {
      id: "actions",
      cell: ({ row }) => (
        <MerchantUserOptions
          setTransactionId={setTransactionId}
          setMerchantUser={() => setMerchantUser(row.original)}
          id={row.original.merchantId}
          onOpenView={onOpenViewModal}
          onOpenInvite={onOpenDeleteModal}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Filter Button */}
      <div className="flex justify-between  items-center mb-4">
        <button
          className="px-4 flex items-center gap-2 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
          onClick={handleSidebarOpen}
        >
          <Filter12Filled className="text-2xl" />
          Filters
        </button>
        <Button
          className="bg-primary-500"
          onClick={onOpenView}>Invite Merchant User</Button>
      </div>
      {/* RHS Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ maxWidth: 360 }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Filter Merchant Users</h2>
          <button
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            onClick={handleSidebarClose}
            aria-label="Close sidebar"
          >
            &times;
          </button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          <form className="space-y-3">
            {filterFields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium mb-1" htmlFor={field.name}>
                  {field.label}
                </label>
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  value={filters[field.name] || ""}
                  onChange={handleFilterChange}
                  className="border rounded px-2 py-1 w-full"
                />
              </div>
            ))}
          </form>
          <button
            type="button"
            className="mt-6 w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
            onClick={() => { setFilters({}); setSidebarOpen(false); }}
          >
            Reset
          </button>
        </div>
      </div>
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
          onClick={handleSidebarClose}
        />
      )}
      <DataTable
        isLoading={isPending}
        columns={columns}
        data={(merchantUsers?.items ?? []) as MerchantUser[]}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {isOpenView && (
        <MerchantUserFormModal onClose={onCloseView} isOpen={isOpenView} />
      )}
      {isOpenDeleteModal && (
        <DeleteMerchantUserModal
          onCancel={onCloseDeleteModal}
          isLoading={isPendingDelete}
          onProceed={() =>
            deleteMerchantUser({
              id: merchantUser._id,
            })
          }
          merchant={merchantUser as any}
        />
      )}
      {isOpenViewModal && (
        <ViewMerchantModal
          onClose={onCloseViewModal}
          merchant={merchantUser as any}
        />
      )}
      {isOpenEditModal && (
        <EditMerchantUserModal
          onCancel={onCloseViewModal}
          merchant={merchantUser as any}
          isOpen={isOpenEditModal}
        />
      )}
    </div>
  );
};

export default MerchantUserPage;
